import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import "sonner/dist/styles.css";
import { QueryClient, QueryClientProvider, type Query } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { UpdateProvider } from "@/contexts/UpdateContext";
import App from "./App";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;
  if (window.location.pathname === "/login") return;

  window.location.href = "/login";
};

// Anti-spam: dedup de toasts dentro de uma janela curta. Sem isso, falhas
// transientes de rede que disparam várias queries em paralelo geram N toasts
// idênticos em ~100ms.
const recentToastKeys = new Map<string, number>();
const TOAST_DEDUP_MS = 4000;
function maybeToastQueryError(message: string) {
  const now = Date.now();
  const last = recentToastKeys.get(message) ?? 0;
  if (now - last < TOAST_DEDUP_MS) return;
  recentToastKeys.set(message, now);
  // Limpa entradas antigas pra não vazar memória em sessões longas.
  if (recentToastKeys.size > 50) {
    for (const [k, t] of recentToastKeys) {
      if (now - t > TOAST_DEDUP_MS * 2) recentToastKeys.delete(k);
    }
  }
  toast.error(message);
}

// Um toast vermelho é uma interrupção: só vale quando o usuário perdeu algo
// que ele estava esperando ver E pode fazer algo a respeito. Estes três casos
// não passam nesse teste — eram a maior parte do ruído que chegava na tela.
function shouldToastQueryError(query: Query, error: unknown): boolean {
  // 0) A própria tela mostra o erro no lugar certo (`meta: { erroNaTela: true }`):
  //    o toast repetiria a mesma frase por cima.
  if ((query.meta as { erroNaTela?: boolean } | undefined)?.erroNaTela) return false;

  // 1) Sessão expirada — já estamos redirecionando pro /login.
  if (error instanceof TRPCClientError && error.message === UNAUTHED_ERR_MSG) return false;

  // 2) FORBIDDEN — o usuário não podia ver esse dado e a tela nem devia ter
  //    pedido. É bug de gating nosso (falta `enabled`/PermissionGuard), não
  //    recado pra quem está usando: ele não tem nenhuma ação a tomar, e a
  //    query costuma ter refetchInterval, o que transformava um bug de UI em
  //    toast recorrente a cada 30s.
  const data = (error as { data?: { code?: string; httpStatus?: number } } | null)?.data;
  if (data?.code === "FORBIDDEN" || data?.httpStatus === 403) {
    console.warn("[API Query FORBIDDEN]", query.queryHash, error);
    return false;
  }

  // 3) Polling de fundo com dado em cache — a tela continua mostrando o
  //    conteúdo anterior e o próximo ciclo resolve sozinho. Avisar de uma
  //    falha que o usuário não percebeu só gera desconfiança.
  //
  //    Só vale para o polling: um refetch que o usuário provocou (salvou algo,
  //    invalidou, e a revalidação falhou) precisa avisar, senão ele fica
  //    olhando dado velho achando que salvou.
  //
  //    `refetchInterval` é opção de observer, não da Query — por isso a leitura
  //    vem de `query.observers` e não de `query.options`.
  const isPolling = query.observers.some((o) => o.options.refetchInterval != null);
  if (isPolling && query.state.data !== undefined) return false;

  return true;
}

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
    // Antes só fazia console.error e o usuário ficava sem qualquer feedback
    // (tela em branco / loader infinito). Agora um toast amigável aparece —
    // mas só quando ele de fato ficou sem os dados. Mutations já têm onError
    // próprios — não toastamos aqui pra evitar duplicação.
    if (shouldToastQueryError(event.query, error)) {
      maybeToastQueryError(toUserFriendlyError(error, "Falha ao carregar dados. Recarregue a página."));
    }
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

// Load analytics script only when env vars are configured
const analyticsEndpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT;
const analyticsWebsiteId = import.meta.env.VITE_ANALYTICS_WEBSITE_ID;
if (analyticsEndpoint && analyticsWebsiteId) {
  const script = document.createElement('script');
  script.defer = true;
  script.src = `${analyticsEndpoint}/umami`;
  script.dataset.websiteId = analyticsWebsiteId;
  document.body.appendChild(script);
}

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <UpdateProvider>
        <App />
      </UpdateProvider>
    </QueryClientProvider>
  </trpc.Provider>
);
